import FormatService from 'modules/Format';
import FetchService from 'modules/Fetch';
import SearchService from 'modules/Search';
import LiveDataService from 'modules/LiveData';
import ValidationService from 'modules/Validation/service';
import Locale from 'modules/Localization';
import { edisService } from 'modules/Edis';
import {
  CommonOrderSide,
  OrderRequestMode,
  OrderType,
  Products,
} from 'modules/Ordering';
import OrdersTVService from 'modules/TradesPW3/Orders';
import {
  OrderingServiceFetch,
  OrderingServiceFormatter,
} from 'modules/Ordering/services';
import { broker } from 'modules/TradingChart/consts';
import AppStore from 'modules/App';
import Validation from 'modules/Validation';
import { getScripforEdis } from 'modules/Edis/util';
import Notifications from 'modules/Notifications';
import { NotificationType } from '../../TradingChart/components/TradingView/sources/charting_library';
import {
  ConversionValuesSchema,
  PositionBase,
  PositionDetails,
  PositionLists,
  PositionV3,
  Product,
  SurveillanceShownMap,
} from './typings';
import Calculation from './helpers/positionsCalculation';
// consts
import {
  BUY,
  convertPositionErrorMessages,
  DEFAULT_POSITION_DETAILS,
  SELL,
} from './constants';
import PositionsPW3 from './store';
import { generateSquareOffData, getPositionSecondLeg, prepareExitOrdersRequestData } from './utils';

class PositionsPW3Service extends PositionsPW3 {
  calc = new Calculation();

  checkPositionExistence = ({
    fillInfo: { cfBuy, cfSell, dayBuy, daySell },
  }: PositionBase): boolean =>
    Number(cfBuy.qty) !== 0 ||
    Number(dayBuy.qty) !== 0 ||
    Number(cfSell.qty) !== 0 ||
    Number(daySell.qty) !== 0;

  getPositions = async () => {
    const response = await FetchService.request<'PORTFOLIO', PositionLists>(
      'PORTFOLIO',
      '/positions',
      'GET_POSITIONS',
      {
        query: {
          nsecom: true,
        },
      },
    );

    const mtfPositions = await this.getMTFPositions();
    if (!response.error && response?.data) {
      const positionData: PositionBase[] = [];
      const positionList = response.data.list;
      const positionListV3 = response.data.listV3;

      // for MTF order positon data available in FillInfo and NetInfo
      if (Array.isArray(mtfPositions.list)) {
        mtfPositions.list.forEach(position => {
          positionData.push({
            ...position,
            instrument_key: position?.instrument?.i ?? position.instrument_key,
            exchangeToken: FormatService.generateExchangeToken(
              position.instrument.e,
              position.instrument.t,
            ),
          });
        });
      }
      if (Array.isArray(mtfPositions.listV3)) {
        mtfPositions.listV3.forEach(position => {
          positionData.push({
            ...position,
            fillInfo: position.fillInfoV3,
            netInfo: position.netInfoV3,
            instrument_key: position?.instrument?.i ?? position.instrument_key,
            exchangeToken: FormatService.generateExchangeToken(
              position.instrument.e,
              position.instrument.t,
            ),
          });
        });
      }
      // for Delivery order position data available in FillInfoV3 and NetInfoV3
      if (Array.isArray(positionListV3)) {
        positionListV3.forEach(delivery => {
          positionData.push({
            ...delivery,
            fillInfo: delivery.fillInfoV3,
            netInfo: delivery.netInfoV3,
            exchangeToken: FormatService.generateExchangeToken(
              delivery.instrument.e,
              delivery.instrument.t,
            ),
          });
        });
      }
      // for all other types of order position data available in FillInfo and NetInfo
      if (Array.isArray(positionList)) {
        positionList.forEach(position => {
          positionData.push({
            ...position,
            exchangeToken: FormatService.generateExchangeToken(
              position.instrument.e,
              position.instrument.t,
            ),
          });
        });
      }

      await this.handleGetPositions({
        data: [...positionData],
      });
    }
  };

  private isV3Position = (position: PositionBase) => {
    if (!position) return false;

    switch (position.product) {
      case 'D':
        // use v3
        return Validation.isEquities(position.instrument.e);
      case 'I':
      case 'CO':
      case 'OCO':
      case 'MTF':
      case 'MTFS':
        // use normal
        return false;
      default:
        return false;
    }
  };

  formatLivePositionData = (positionData: PositionV3): PositionBase => {
    const isV3 = this.isV3Position(positionData);
    return {
      ...positionData,
      instrument_key: positionData.instrument_key,
      fillInfo: isV3 ? positionData.fillInfoV3 : positionData.fillInfo,
      netInfo: isV3 ? positionData.netInfoV3 : positionData.netInfo,
      exchangeToken: FormatService.generateExchangeToken(
        positionData.instrument.e,
        positionData.instrument.t,
      ),
    } as PositionBase;
  };

  getMTFPositions = async (): Promise<PositionLists> => {
    const response = await FetchService.request<'PORTFOLIO', PositionLists>(
      'PORTFOLIO',
      '/mtf',
      'GET_POSITIONS',
    );

    if (!response.error && response.data) {
      return response.data;
    }

    return { list: [], listV3: [] };
  };

  handleGetPositions = async (message: {
    data: PositionBase[];
  }): Promise<void> => {
    // Pay attention, notifications has been removed!
    if (message.data) {
      const formattedPositions =
        message.data &&
        (await Promise.all(message.data.map(this.modifyPosition)));
      this.setPositions(formattedPositions);
    }
  };

  modifyPosition = async (position: PositionBase) => {
    const exchangeToken = FormatService.generateExchangeToken(
      position.instrument.e,
      position.instrument.t,
      '|',
    );

    const scripData = await SearchService.searchByExchangeToken(exchangeToken);
    const tvExchangeToken = FormatService.generateCustomTvExchangeToken(
      scripData?.displayName || '',
      exchangeToken || '',
    );

    return {
      ...position,
      name: scripData?.symbol || '',
      exchangeToken,
      tvExchangeToken,
      instrumentKey: scripData?.instrumentKey,
    };
  };

  generatePositionDetails = (singlePosition: PositionBase): PositionDetails => {
    const liveData = LiveDataService.getByInstrumentKey(
      singlePosition?.instrument_key || '',
    );

    if (singlePosition) {
      const ltp = liveData?.lastTradedPrice;
      const notNullLtp = ltp || '';

      const { unrealized, realized, total } = this.calc.getMtmAndPnlValues(
        singlePosition,
        notNullLtp,
        liveData?.close || 0,
      );

      const { netBuyQty, netSellQty, netQty } = this.calc.getNetQuantity(
        singlePosition,
      );

      const avgBuyPrice = singlePosition?.fillInfo?.dayBuy?.avgPrice ?? 0;
      const avgSellPrice = singlePosition?.fillInfo?.daySell?.avgPrice ?? 0;

      return {
        netBuyQty,
        netSellQty,
        netQty,
        avgBuyPrice,
        avgSellPrice,
        avgPrice: singlePosition.netInfo.netAvgPrice,
        buyValue: Number(singlePosition?.fillInfo?.dayBuy?.amt ?? 0),
        sellValue: Number(singlePosition?.fillInfo?.daySell?.amt ?? 0),
        pnl: {
          total: ltp ? total.pnl : 0,
          realized: ltp ? realized.pnl : 0,
          unrealized: ltp ? unrealized.pnl : 0,
        },
        product: this.getProductValue(singlePosition.product) as Product,
        avgCfPrice: !singlePosition?.fillInfo?.cfBuy?.qty
          ? '0'
          : String(singlePosition?.netInfo?.netAvgPrice),
        multiplier: singlePosition?.instrument?.m ?? 1,
      };
    }

    return DEFAULT_POSITION_DETAILS;
  };

  // get product value
  getProductValue = (code: string): string => {
    switch (code) {
      case 'I':
        return 'Intraday';
      case 'D':
        return 'Delivery';
      default:
        return code;
    }
  };

  aggregatePnlData = (position: PositionBase) => {
    const liveData = LiveDataService.getByInstrumentKey(
      position?.instrument_key || '',
    );
    const notNullLtp = liveData?.lastTradedPrice || '';

    if (!position) {
      return {
        daily: '-',
        total: '-',
      };
    }
    const { total } = this.calc.getMtmAndPnlValues(
      position,
      notNullLtp,
      liveData?.close || 0,
    );

    return {
      daily: notNullLtp ? total.mtm : 0,
      total: notNullLtp ? total.pnl : 0,
    };
  };

  getGlobalPnl = (positions: PositionBase[]) =>
    positions.reduce(
      (acc, item) => {
        const currentPositionPnl = this.aggregatePnlData(item);

        return {
          daily: FormatService.getRoundOffToTwoDecimal(
            Number(acc.daily) + Number(currentPositionPnl.daily),
          ),
          total: FormatService.getRoundOffToTwoDecimal(
            Number(acc.total) + Number(currentPositionPnl.total),
          ),
        };
      },
      { daily: 0, total: 0 },
    );

  squareOffCOPosition = async (instrumentKey: string) => {
    const secondLegsOrderIds = getPositionSecondLeg(
      OrdersTVService.orders,
      instrumentKey,
    );
    if (secondLegsOrderIds.length) {
      await OrdersTVService.cancelMultiOrders(secondLegsOrderIds);
    }
  };

  preClosePosition = async (
    position: PositionBase,
    fields: { limitPrice?: number; orderType: OrderType },
    underSurveillance = false,
  ) => {
    if (position.product === 'OCO' || position.product === 'CO') {
      await this.squareOffCOPosition(position.instrument_key);
      AppStore.toggleModal('closePosition');
    } else {
      const squareOffOrderData = generateSquareOffData(
        position,
        underSurveillance,
        fields.limitPrice,
      );
      const orderData = {
        context: 'TV',
        requestMode: OrderRequestMode.REGULAR,
        orderTime: Date.now(),
        orders: [squareOffOrderData],
      };

      const isEdisNeeded = ValidationService.isEdisRequired(
        position.instrument.e,
        (Products[position.product] || position.product) as OrderEntryType,
        orderData.orders[0].side === 'BUY' ? 'Buy' : 'Sell',
      );

      let response: HttpResponse<CommonOrdersResponse>;

      if (isEdisNeeded) {
        const scrips = await getScripforEdis(
          position.instrument.e,
          `${position.instrument.t}`,
          Number(orderData.orders[0].quantity),
          0);
        const isEdisSuccess = await edisService.authoriseEdis(scrips);
        AppStore.toggleModal('closePosition');

        if (isEdisSuccess) {
          response = await OrderingServiceFetch.placeOrderRequest(
            orderData,
          );
        } else {
          return;
        }
      } else {
        response = await OrderingServiceFetch.placeOrderRequest(
          orderData,
        );
        AppStore.toggleModal('closePosition');
      }

      if (response?.success) {
         const scripData = await SearchService.searchByInstrumentKey(
          orderData.orders[0].instrumentKey,
        );

        OrderingServiceFormatter.sendMixpanelEventForCommonOrderPlaced(
          'Placed',
          orderData.orders[0],
          scripData,
          broker.instance?.isInstantOrderEnabled?.() ?? false,
        );
      }

      if (broker.instance) {
        if (!response?.success) {
          broker.instance.showNotification(
            'Failure',
            response.error?.message,
            NotificationType.Error,
          );
        }
      }
    }
  };

  multiPositionSquareOff = (
    positionsToExit: PositionBase[],
    isSurveillanceShownMap: SurveillanceShownMap,
  ) => {
    const {
      placeOrderRequestList,
      cancelOrderRequestList,
    } = prepareExitOrdersRequestData(positionsToExit, isSurveillanceShownMap);
    if (cancelOrderRequestList.length) {
      OrdersTVService.cancelMultiOrders(cancelOrderRequestList);
    }

    if (placeOrderRequestList.length) {
      const exitOrdersRequestData: CommonOrdersRequest = {
        context: 'TV',
        requestMode: OrderRequestMode.REGULAR,
        orders: placeOrderRequestList,
        orderTime: Date.now(),
      };

      OrdersTVService.placeBasketOrder(exitOrdersRequestData);
    }
  };

  openConvertPositionModal = (positionId: string) => {
    this.setConvertingPositionId(positionId);
    AppStore.toggleModal('convertPosition');
  };

  private getConversionProductValue = (product: string) => {
    switch (product.toLowerCase()) {
      case 'i':
      case 'mtf':
      case 'mtfs':
        return 'D';
      default:
        return 'I';
    }
  };

  private generatePositionsConversionData = (
    dataForConversion: ConversionValuesSchema,
    position: PositionBase,
  ): ConvertPositionReqData => {
    const { product, instrument_key: instrumentKey } = position;
    const {
      amount,
      quantity_to_convert: quantityToConvert,
      quantity,
      is_cf: cf,
    } = dataForConversion;
    const netQty = this.calc.getNetQty(position);
    const transactionType = netQty > 0 ? CommonOrderSide.B : CommonOrderSide.S;

    return {
      requestMode: OrderRequestMode.REGULAR,
      context: 'TV',
      orderTime: Date.now(),
      orders: [{
        instrumentKey,
        product: this.getConversionProductValue(product),
        oldProduct: product,
        side: transactionType,
        quantity: Number(quantity),
        quantityToConvert: Number(quantityToConvert),
        amount: Number(amount),
        cf,
      }],
    };
  };

  // generate request data for convert partial position request
  private convertData = (
    isCf: boolean,
    quantityToConvert: number,
    amount: number,
    quantity: number,
  ) => {
    const dataForConversion = {
      amount: amount.toString(),
      is_cf: isCf,
      quantity_to_convert: quantityToConvert.toString(),
      quantity: quantity.toString(),
    }; // part of data that needed to convert position

    return (position: PositionBase) =>
      this.generatePositionsConversionData(dataForConversion, position);
  };

  private convertPosition = async (data: ConvertPositionReqData) => {
    const response = await OrderingServiceFetch.convertPositionRequest(data);

    if (!response?.success || response?.data?.responseDetailList?.[0]?.status === 'R') {
      Notifications.pushToast('POSITION_CONVERTED', {
        title: Locale.translate('POSITION_CONVERSION_FAILED_TITLE'),
        text:
        response?.error?.message ??
        response.data?.responseDetailList?.[0]?.message ??
        Locale.translate('DEFAULT_ERROR_MESSAGE'),
        type: 'error',
      });
      return;
    }
    if (response?.success) {
      Notifications.pushToast('POSITION_CONVERTED', {
        title: Locale.translate('POSITION_CONVERSION_SUCCESS_TITLE'),
        text:
          Locale.translate('POSITION_CONVERSION_SUCCESS_MESSAGE'),
      });
    }
  };

  private convertTransaction = (
    position: PositionBase,
    qtyToChange: number,
    isBuy: boolean,
  ) => {
    const { fillInfo } = position;
    const cfTx = isBuy ? fillInfo.cfBuy : fillInfo.cfSell;
    const dayTx = isBuy ? fillInfo.dayBuy : fillInfo.daySell;

    if (cfTx.qty) {
      if (qtyToChange > cfTx.qty) {
        this.convertPosition(
          this.convertData(true, cfTx.qty, cfTx.amt, cfTx.qty)(position),
        );
        this.convertPosition(
          this.convertData(
            false,
            qtyToChange - cfTx.qty,
            dayTx.amt,
            dayTx.qty,
          )(position),
        );
      } else {
        this.convertPosition(
          this.convertData(true, qtyToChange, cfTx.amt, cfTx.qty)(position),
        );
      }
    } else {
      this.convertPosition(
        this.convertData(false, qtyToChange, dayTx.amt, dayTx.qty)(position),
      );
    }
  };

  private convertBuyTx = (position: PositionBase, qtyToChange: number) => {
    this.convertTransaction(position, qtyToChange, true);
  };

  private convertSellTx = (position: PositionBase, qtyToChange: number) => {
    this.convertTransaction(position, qtyToChange, false);
  };

  startPositionConversion = (
    uniqueId: string | number,
    qtyToChange: string,
  ) => {
    const position = this.getPositionByUniqueId(
      this.positions,
      uniqueId.toString(),
    );

    if (position) {
      const netQty = this.calc.getNetQty(position);
      const transactionType = netQty > 0 ? BUY : SELL;

      switch (transactionType) {
        case BUY:
          this.convertBuyTx(position, Number(qtyToChange));
          break;
        default:
          this.convertSellTx(position, Number(qtyToChange));
          break;
      }
    }
  };

  isPositionConvertible = (positionId: string) => {
    const position = this.getPositionByUniqueId(this.positions, positionId);
    if (!position) {
      return false;
    }
    const { product } = position;
    const netQty = this.calc.getNetQty(position);
    const isConvertPositionAvailable =
      (product === 'I' || product === 'D') && netQty !== 0;
    return isConvertPositionAvailable;
  }

  /**
   * Validate qty input value from convert position modal
   * @param  {string} qty new qty
   */
  isQtyInputValid = (qty: string) =>
    !ValidationService.hasAlpha(qty) &&
    !Number.isNaN(+qty) &&
    Number.isInteger(+qty) &&
    !qty.includes(',') &&
    !qty.includes('.');

  private validatePosition(position: PositionBase | undefined) {
    if (!position?.exchangeToken || !position?.instrument) {
      return {
        status: false,
        message: 'Position not found',
      };
    }
    return { status: true, message: '' };
  }

  private validateLotSize(lotSize: number | undefined) {
    if (!lotSize || lotSize.toString() === '-') {
      return {
        status: false,
        message: convertPositionErrorMessages().emptyLotSize,
      };
    }
    return { status: true, message: '' };
  }

  private validateQuantity(
    qtyToChange: string,
    instrument: Instrument,
    lotSize: number | undefined,
    netQty: number,
  ) {
    if (!this.isQtyInputValid(qtyToChange)) {
      return {
        status: false,
        message: convertPositionErrorMessages().incorrectValue,
      };
    }

    if (qtyToChange === '' || Number(qtyToChange) <= 0) {
      return {
        status: false,
        message: convertPositionErrorMessages().lessThanOne,
      };
    }

    if (
      lotSize &&
      Number(qtyToChange) % lotSize !== 0 &&
      !Validation.isCommodity(instrument.e)
    ) {
      return {
        status: false,
        message: convertPositionErrorMessages(lotSize).multiplierError,
      };
    }

    if (netQty.toString() === '-') {
      return {
        status: false,
        message: convertPositionErrorMessages().insufficientQty,
      };
    }

    if (Number(qtyToChange) > Math.abs(Number(netQty))) {
      return {
        status: false,
        message: convertPositionErrorMessages().lessOrEqual,
      };
    }

    return { status: true, message: '' };
  }

  validateUserInput = async (qtyToChange: string, id: string) => {
    const position = this.getPositionByUniqueId(this.positions, id);

    // Validate position exists
    const positionValidation = this.validatePosition(position);
    if (!positionValidation.status) return positionValidation;

    // At this point we know position is defined
    const validPosition = position as PositionBase; // Type assertion
    const { exchangeToken, instrument } = validPosition;
    const validExchangeToken = exchangeToken as string;
    const validInstrument = instrument as Instrument;

    // Get required data
    const netQty = this.calc.getNetQty(validPosition);
    const scripData = await SearchService.searchByExchangeToken(
      validExchangeToken,
    );
    const { lotSize } = scripData || {};

    // Validate lot size
    const lotSizeValidation = this.validateLotSize(lotSize);
    if (!lotSizeValidation.status) return lotSizeValidation;

    // Validate quantity
    return this.validateQuantity(qtyToChange, validInstrument, lotSize, netQty);
  };
}

export default new PositionsPW3Service();
